import React, { useState, useEffect } from 'react';
import { 
  Plus, X, Settings, Boxes, Package, 
  FileText, Archive, Search, RefreshCcw,
  ChevronDown, ChevronRight, AlertTriangle, CheckCircle2, Layers
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import Sidebar from '../../components/Sidebar';
import API, { socket } from '../../services/api';

const ListeFT = () => {
  const navigate = useNavigate();
  
  // --- États ---
  const [fichesTechniques, setFichesTechniques] = useState([]);
  const [expandedId, setExpandedId] = useState(null);
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [activeTab, setActiveTab] = useState('actif');
  const [searchTerm, setSearchTerm] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  
  const [produitsFinis, setProduitsFinis] = useState([]);
  const [produitsSemiFinis, setProduitsSemiFinis] = useState([]);

  // État pour la notification Toast intégrée
  const [notification, setNotification] = useState({
    isOpen: false,
    type: 'success', // 'success', 'error', 'confirm'
    message: '',
    onConfirm: null
  });

  const [newFt, setNewFt] = useState({ 
    nom: '', 
    type_cible: 'fini', 
    cible_id: '', 
    quantite_base: 1 
  });

  const primaryColor = '#7c3aed';
  const borderColor = '#e2e8f0';

  const s = {
    layout: { display: 'flex', height: '100vh', overflow: 'hidden' },
    mainArea: { flex: 1, overflow: 'hidden', background: '#f1f5f9', display: 'flex', flexDirection: 'column', position: 'relative' },
    formPanel: {
      background: 'white',
      maxHeight: isFormOpen ? '480px' : '0',
      overflow: 'hidden',
      transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
      borderBottom: isFormOpen ? `3px solid ${primaryColor}` : '0px solid transparent',
      padding: isFormOpen ? '25px 40px' : '0 40px',
      boxShadow: '0 10px 15px -3px rgba(0,0,0,0.1)',
      flexShrink: 0
    },
    input: { padding: '12px', borderRadius: '8px', border: `1px solid ${borderColor}`, fontSize: '14px', width: '100%', marginTop: '5px', outlineColor: primaryColor, background: 'white' },
    label: { fontSize: '11px', fontWeight: '800', color: '#64748b', textTransform: 'uppercase' },
    tab: (active) => ({
      padding: '16px 24px', cursor: 'pointer',
      borderBottom: active ? `3px solid ${primaryColor}` : '3px solid transparent',
      color: active ? primaryColor : '#64748b', fontWeight: active ? '800' : '600',
      display: 'flex', alignItems: 'center', gap: '10px', transition: '0.2s'
    }),
    actionBtn: { border: 'none', padding: '8px 12px', borderRadius: '8px', cursor: 'pointer', fontWeight: '800', display: 'flex', alignItems: 'center', gap: '6px', transition: '0.2s', fontSize: '11px' },
    detailRow: { background: '#f8fafc', borderBottom: `1px solid ${borderColor}` },
    
    toastContainer: {
      position: 'absolute', top: '20px', left: '50%', transform: 'translateX(-50%)',
      zIndex: 1000, display: 'flex', alignItems: 'center',
      background: 'white', padding: '12px 20px', borderRadius: '15px',
      boxShadow: '0 20px 25px -5px rgba(0,0,0,0.1)',
      border: `1px solid ${borderColor}`,
      minWidth: '400px',
      transition: 'all 0.3s ease'
    },
    confirmBtn: { background: primaryColor, color: 'white', border: 'none', padding: '6px 15px', borderRadius: '6px', fontWeight: '800', cursor: 'pointer', fontSize: '11px' },
    cancelBtn: { background: '#f1f5f9', color: '#64748b', border: 'none', padding: '6px 15px', borderRadius: '6px', fontWeight: '800', cursor: 'pointer', fontSize: '11px' }
  };

  useEffect(() => {
    fetchInitialData();
    const handleGlobalUpdate = (event) => {
        const { table } = event.detail;
        if (['fiches_techniques', 'products', 'psf', 'all'].includes(table)) {
            fetchInitialData();
        }
    };
    window.addEventListener('ERP_DATA_CHANGED', handleGlobalUpdate);
    return () => window.removeEventListener('ERP_DATA_CHANGED', handleGlobalUpdate);
  }, []);

  useEffect(() => {
    if (notification.isOpen && notification.type !== 'confirm') {
      const timer = setTimeout(() => {
        setNotification(prev => ({ ...prev, isOpen: false }));
      }, 4000);
      return () => clearTimeout(timer);
    }
  }, [notification.isOpen, notification.type]);

  const showToast = (message, type = 'success') => {
    setNotification({ isOpen: true, type, message, onConfirm: null });
  };

  const showConfirm = (message, onConfirmAction) => {
    setNotification({ isOpen: true, type: 'confirm', message, onConfirm: onConfirmAction });
  };

  const fetchInitialData = async () => {
    setIsLoading(true);
    try {
        const [ftRes, prodRes, psfRes] = await Promise.all([
            API.get('/technical-sheets').catch(() => ({ data: [] })),
            API.get('/products').catch(() => ({ data: [] })),
            API.get('/semi-finished-products').catch(() => ({ data: [] }))
        ]);

        const fichesProtegees = (ftRes.data || []).map(ft => {
            const itemsValides = (ft.items || []).map(item => {
                const pu = Number(item.prix_unitaire || item.prix || item.pu || 0);
                const qte = Number(item.quantite_requise || item.quantite || item.qte || 0);
                return { ...item, prix_unitaire: pu, quantite_requise: qte, total_item: pu * qte };
            });
            const coutTotalLot = itemsValides.reduce((acc, item) => acc + item.total_item, 0);
            const qBase = Number(ft.quantite_base) > 0 ? Number(ft.quantite_base) : 1;
            
            const coutUnitaireFinal = coutTotalLot / qBase;

            return { 
                ...ft, 
                items: itemsValides, 
                cout_total: coutTotalLot, 
                cout_unitaire: coutUnitaireFinal,
                quantite_base: qBase 
            };
        });
        
        setFichesTechniques(fichesProtegees);
        setProduitsFinis(Array.isArray(prodRes.data) ? prodRes.data.filter(p => p.is_active === 1) : []);
        setProduitsSemiFinis(Array.isArray(psfRes.data) ? psfRes.data.filter(p => p.is_active === 1) : []);
    } catch (err) {
        showToast("Erreur de synchronisation des données.", "error");
    } finally {
        setIsLoading(false);
    }
  };

  const handleCreateHeader = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    const dataToSend = {
      id: `FT-${Date.now().toString().slice(-6)}`,
      nom: newFt.nom.toUpperCase(),
      product_id: newFt.type_cible === 'fini' ? newFt.cible_id : null,
      semi_fini_id: newFt.type_cible === 'semi-fini' ? newFt.cible_id : null,
      quantite_base: parseFloat(newFt.quantite_base) || 1,
      company_id: "COMP-01",
      is_active: 1
    };
    try {
      await API.post('/technical-sheets', dataToSend);
      showToast("La fiche technique a été créée.", "success");
      setNewFt({ nom: '', type_cible: 'fini', cible_id: '', quantite_base: 1 });
      setIsFormOpen(false);
      fetchInitialData();
    } catch (err) {
      showToast(err.response?.data?.error || "Erreur de création", "error");
    } finally {
      setIsLoading(false);
    }
  };

  const toggleStatus = (ft) => {
    const nomFT = (ft.nom || "").toUpperCase();
    if (ft.is_active === 0 && nomFT.includes("VERSIONNÉ")) {
        showToast("Cette version obsolète ne peut pas être restaurée.", "error");
        return;
    }
    const action = ft.is_active === 1 ? "archiver" : "restaurer";
    showConfirm(
      `Voulez-vous vraiment ${action} la fiche technique "${ft.nom}" ?`,
      async () => {
        try {
            await API.patch(`/technical-sheets/${ft.id}`, { is_active: ft.is_active === 1 ? 0 : 1 });
            setNotification({ isOpen: false });
            showToast(`Fiche technique ${action}e !`, "success");
            fetchInitialData();
        } catch (err) {
            showToast("Échec de la modification.", "error");
        }
      }
    );
  };

  const getCibleNom = (ft) => {
    if (ft.product_id) {
      const p = produitsFinis.find(item => item.id === ft.product_id);
      return p ? p.nom : 'Produit inconnu';
    }
    if (ft.semi_fini_id) {
      const s = produitsSemiFinis.find(item => item.id === ft.semi_fini_id);
      return s ? s.nom : 'PSF inconnu';
    }
    return 'Non défini';
  };

  const handleConfigClick = (ft) => {
    const exclusionId = ft.semi_fini_id || ft.product_id;
    const typeCible = ft.semi_fini_id ? 'semi-fini' : 'fini';
    navigate('/analytique/ft/parametrer', { 
      state: { ftId: ft.id, ftNom: ft.nom, exclusionId, typeCible, quantiteBase: ft.quantite_base } 
    });
  };

  const filteredFiches = fichesTechniques.filter(ft => 
    (activeTab === 'actif' ? ft.is_active === 1 : ft.is_active === 0) &&
    (ft.nom.toLowerCase().includes(searchTerm.toLowerCase()) || 
     ft.id.toLowerCase().includes(searchTerm.toLowerCase()) ||
     getCibleNom(ft).toLowerCase().includes(searchTerm.toLowerCase()))
  );

  const NotificationToast = () => {
    if (!notification.isOpen) return null;
    const isConfirm = notification.type === 'confirm';
    const isError = notification.type === 'error';
    const color = isError ? '#ef4444' : (isConfirm ? '#f59e0b' : '#10b981');
    return (
      <div style={{ ...s.toastContainer, borderLeft: `8px solid ${color}` }}>
        <div style={{ 
            display: 'flex', justifyContent: 'center', alignItems: 'center', 
            width: '35px', height: '35px', borderRadius: '50%', 
            background: `${color}15`, marginRight: '15px', flexShrink: 0
        }}>
          {isError ? <X size={20} color={color} /> : 
           isConfirm ? <AlertTriangle size={20} color={color} /> : 
           <CheckCircle2 size={20} color={color} />}
        </div>
        <div style={{ flex: 1 }}>
          <p style={{ margin: 0, fontWeight: '700', color: '#1e293b', fontSize: '14px' }}>{notification.message}</p>
          {isConfirm && (
            <div style={{ display: 'flex', gap: '10px', marginTop: '10px' }}>
              <button onClick={notification.onConfirm} style={s.confirmBtn}>OUI, CONTINUER</button>
              <button onClick={() => setNotification({isOpen: false})} style={s.cancelBtn}>ANNULER</button>
            </div>
          )}
        </div>
        {!isConfirm && (
          <button onClick={() => setNotification({isOpen: false})} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#cbd5e1' }}>
            <X size={20} />
          </button>
        )}
      </div>
    );
  };

  return (
    <div style={s.layout}>
      <Sidebar />
      <main style={s.mainArea}>
        <NotificationToast />
        
        <header style={{ background: 'white', padding: '20px 40px', borderBottom: `1px solid ${borderColor}`, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexShrink: 0, zIndex: 100 }}>
          <h1 style={{ margin: 0, fontSize: '20px', fontWeight: '900' }}>
            <span style={{color: primaryColor}}>FICHES</span> TECHNIQUES
          </h1>
          <div style={{ display: 'flex', gap: '12px' }}>
            <div style={{ position: 'relative' }}>
              <Search style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', color: '#94a3b8' }} size={18} />
              <input type="text" placeholder="Rechercher..." style={{ padding: '11px 40px', borderRadius: '10px', border: `1px solid ${borderColor}`, width: '250px' }} value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} />
            </div>
            <button onClick={() => setIsFormOpen(!isFormOpen)} style={{ background: primaryColor, color: 'white', padding: '10px 20px', borderRadius: '10px', border: 'none', cursor: 'pointer', fontWeight: '800', display: 'flex', alignItems: 'center', gap: '8px' }}>
              {isFormOpen ? <X size={18} /> : <Plus size={18} />} {isFormOpen ? 'ANNULER' : 'NOUVELLE FT'}
            </button>
          </div>
        </header>

        {/* Panneau de création */}
        <div style={s.formPanel}>
            <form onSubmit={handleCreateHeader} style={{ display: 'grid', gridTemplateColumns: '2fr 1.2fr 1fr 1fr auto', gap: '15px', alignItems: 'end' }}>
                <div>
                    <label style={s.label}>Nom de la Recette</label>
                    <input required style={s.input} placeholder="Ex: YAOURT NATURE" value={newFt.nom} onChange={e => setNewFt({...newFt, nom: e.target.value})} />
                </div>
                <div>
                    <label style={s.label}>Type Destination</label>
                    <select style={s.input} value={newFt.type_cible} onChange={e => setNewFt({...newFt, type_cible: e.target.value, cible_id: ''})}>
                        <option value="fini">Produit Fini (Vente)</option>
                        <option value="semi-fini">Produit Semi-Fini (Composant)</option>
                    </select>
                </div>
                <div>
                    <label style={s.label}>Qté de Base (Rendement)</label>
                    <input 
                      type="number" 
                      step="any" 
                      min="0.0001" 
                      required 
                      style={s.input} 
                      value={newFt.quantite_base} 
                      onChange={e => setNewFt({...newFt, quantite_base: e.target.value})} 
                    />
                </div>
                <div>
                    <label style={s.label}>Sélectionner Cible</label>
                    <select 
                      required 
                      style={{ ...s.input, maxHeight: '150px', overflowY: 'auto' }} 
                      value={newFt.cible_id} 
                      onChange={e => setNewFt({...newFt, cible_id: e.target.value})}
                    >
                        <option value="">Choisir...</option>
                        {newFt.type_cible === 'fini' 
                        ? produitsFinis.map(p => <option key={p.id} value={p.id}>{p.nom}</option>) 
                        : produitsSemiFinis.map(p => <option key={p.id} value={p.id}>{p.nom}</option>)
                        }
                    </select>
                </div>
                <button disabled={isLoading} type="submit" style={{ background: primaryColor, color: 'white', border: 'none', padding: '13px 25px', borderRadius: '8px', fontWeight: '900', cursor: 'pointer' }}>
                    {isLoading ? 'EN COURS...' : 'CRÉER FT'}
                </button>
            </form>
        </div>

        {/* Zone principale contenant les onglets et le tableau */}
        <div style={{ padding: '30px 40px', flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
          <div style={{ display: 'flex', background: 'white', borderRadius: '12px 12px 0 0', border: `1px solid ${borderColor}`, borderBottom: 'none', width: 'fit-content', flexShrink: 0 }}>
            <div style={s.tab(activeTab === 'actif')} onClick={() => setActiveTab('actif')}>
              <FileText size={18} /> Actives ({fichesTechniques.filter(f => f.is_active === 1).length})
            </div>
            <div style={s.tab(activeTab === 'archive')} onClick={() => setActiveTab('archive')}>
              <Archive size={18} /> Archives ({fichesTechniques.filter(f => f.is_active === 0).length})
            </div>
          </div>

          <div style={{ background: 'white', borderRadius: '0 12px 12px 12px', border: `1px solid ${borderColor}`, boxShadow: '0 4px 6px -1px rgba(0,0,0,0.05)', display: 'flex', flexDirection: 'column', overflow: 'hidden', flex: 1 }}>
            
            {/* CORRECTION : Utilisation de <colgroup> pour fixer strictement la largeur de chaque colonne de manière identique dans le thead et le tbody */}
            <div style={{ overflowY: 'auto', maxHeight: '540px', width: '100%' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', tableLayout: 'fixed' }}>
                <colgroup>
                  <col style={{ width: '50px' }} />
                  <col style={{ width: '110px' }} />
                  <col style={{ width: 'auto' }} />
                  <col style={{ width: '140px' }} />
                  <col style={{ width: '180px' }} />
                  <col style={{ width: '140px' }} />
                  <col style={{ width: '130px' }} />
                  <col style={{ width: '220px' }} />
                </colgroup>
                <thead>
                  <tr style={{ background: '#f8fafc', textAlign: 'left', borderBottom: `1px solid ${borderColor}` }}>
                    <th style={{ padding: '15px' }}></th>
                    <th style={{ padding: '15px', color: '#475569', fontSize: '11px', fontWeight: '800' }}>ID FT</th>
                    <th style={{ padding: '15px', color: '#475569', fontSize: '11px', fontWeight: '800' }}>NOM / RECETTE</th>
                    <th style={{ padding: '15px', color: '#475569', fontSize: '11px', fontWeight: '800' }}>DESTINATION</th>
                    <th style={{ padding: '15px', color: '#475569', fontSize: '11px', fontWeight: '800' }}>ARTICLE LIÉ</th>
                    <th style={{ padding: '15px', color: '#475569', fontSize: '11px', fontWeight: '800', textAlign: 'center' }}>RENDEMENT (BASE)</th>
                    <th style={{ padding: '15px', color: '#475569', fontSize: '11px', fontWeight: '800', textAlign: 'right', whiteSpace: 'nowrap' }}>COÛT UNITAIRE</th>
                    <th style={{ padding: '15px', textAlign: 'right', color: '#475569', fontSize: '11px', fontWeight: '800' }}>ACTIONS</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredFiches.map(ft => {
                    const isVersionned = ft.nom && ft.nom.toUpperCase().includes("VERSIONNÉ");
                    return (
                      <React.Fragment key={ft.id}>
                        <tr 
                          style={{ borderBottom: `1px solid ${borderColor}`, cursor: 'pointer', background: expandedId === ft.id ? '#f5f3ff' : 'transparent' }} 
                          onClick={() => setExpandedId(expandedId === ft.id ? null : ft.id)}
                        >
                          <td style={{ padding: '15px', textAlign: 'center', color: '#94a3b8' }}>
                            {expandedId === ft.id ? <ChevronDown size={20} /> : <ChevronRight size={20} />}
                          </td>
                          <td style={{ padding: '15px', color: primaryColor, fontWeight: '700', fontFamily: 'monospace', fontSize: '12px' }}>{ft.id}</td>
                          <td style={{ padding: '15px', fontWeight: '800', color: '#0f172a', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{ft.nom}</td>
                          <td style={{ padding: '15px' }}>
                            <span style={{ 
                              padding: '4px 10px', borderRadius: '20px', fontSize: '11px', fontWeight: '800',
                              background: ft.product_id ? '#dcfce7' : '#fef9c3',
                              color: ft.product_id ? '#166534' : '#854d0e',
                              display: 'inline-flex', alignItems: 'center', gap: '5px'
                            }}>
                              {ft.product_id ? <Package size={12} /> : <Boxes size={12} />}
                              {ft.product_id ? "PRODUIT FINI" : "SEMI-FINI"}
                            </span>
                          </td>
                          <td style={{ padding: '15px', fontSize: '12px', fontWeight: '600', color: '#64748b', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{getCibleNom(ft)}</td>
                          <td style={{ padding: '15px', textAlign: 'center' }}>
                            <span style={{ background: '#ede9fe', color: primaryColor, padding: '4px 10px', borderRadius: '6px', fontWeight: '800', fontSize: '12px', whiteSpace: 'nowrap' }}>
                              {ft.quantite_base} unité(s)
                            </span>
                          </td>
                          <td style={{ padding: '15px', textAlign: 'right', fontWeight: '900', color: '#ef4444', whiteSpace: 'nowrap' }}>
                            {Math.round(ft.cout_unitaire || 0).toLocaleString()} <span style={{fontSize: '10px', color: '#64748b'}}>FCFA</span>
                          </td>
                          <td style={{ padding: '15px', textAlign: 'right' }}>
                            <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end', flexWrap: 'nowrap' }}>
                              <button 
                                disabled={ft.is_active === 0} 
                                onClick={(e) => { e.stopPropagation(); handleConfigClick(ft); }} 
                                style={{ ...s.actionBtn, background: ft.is_active === 0 ? '#f8fafc' : '#ede9fe', color: ft.is_active === 0 ? '#cbd5e1' : primaryColor, cursor: ft.is_active === 0 ? 'not-allowed' : 'pointer' }}
                              >
                                <Settings size={14} /> CONFIGURER
                              </button>
                              <button 
                                disabled={ft.is_active === 0 && isVersionned}
                                onClick={(e) => { e.stopPropagation(); toggleStatus(ft); }} 
                                style={{ ...s.actionBtn, background: ft.is_active ? '#f1f5f9' : (isVersionned ? '#f8fafc' : '#dcfce7'), color: ft.is_active ? '#64748b' : (isVersionned ? '#cbd5e1' : '#059669') }}
                              >
                                {ft.is_active ? <Archive size={14} /> : <RefreshCcw size={14} />}
                                {ft.is_active ? 'ARCHIVER' : 'RESTAURER'}
                              </button>
                            </div>
                          </td>
                        </tr>
                        {expandedId === ft.id && (
                          <tr style={s.detailRow}>
                            <td colSpan="8" style={{ padding: '20px 60px' }}>
                              <div style={{ background: 'white', borderRadius: '10px', border: `1px solid ${borderColor}`, overflow: 'hidden', padding: '15px' }}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px', borderBottom: `1px solid ${borderColor}`, paddingBottom: '8px' }}>
                                  <span style={{ fontSize: '12px', fontWeight: '800', color: '#475569' }}>
                                    Détail des ingrédients pour un lot de référence de <strong style={{color: primaryColor}}>{ft.quantite_base} unité(s)</strong>
                                  </span>
                                  <span style={{ fontSize: '12px', fontWeight: '800', color: '#475569' }}>
                                    Coût Total du Lot : <strong style={{color: '#ef4444'}}>{Math.round(ft.cout_total || 0).toLocaleString()}</strong>
                                  </span>
                                </div>
                                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                                  <thead style={{ background: '#f1f5f9' }}>
                                    <tr>
                                      <th style={{ padding: '10px', textAlign: 'left', fontSize: '10px', color: '#64748b' }}>COMPOSANT</th>
                                      <th style={{ padding: '10px', textAlign: 'center', fontSize: '10px', color: '#64748b' }}>QTÉ REQUISE (POUR {ft.quantite_base} UNITÉS)</th>
                                      <th style={{ padding: '10px', textAlign: 'right', fontSize: '10px', color: '#64748b' }}>P.U</th>
                                      <th style={{ padding: '10px', textAlign: 'right', fontSize: '10px', color: '#64748b' }}>TOTAL LIGNE</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {ft.items && ft.items.length > 0 ? ft.items.map((item, idx) => (
                                      <tr key={idx} style={{ borderBottom: idx === ft.items.length - 1 ? 'none' : `1px solid ${borderColor}` }}>
                                        <td style={{ padding: '10px', fontSize: '13px', fontWeight: '700' }}>{item.designation}</td>
                                        <td style={{ padding: '10px', textAlign: 'center', fontSize: '13px' }}>{item.quantite_requise} {item.unite || 'U'}</td>
                                        <td style={{ padding: '10px', textAlign: 'right', fontSize: '13px', color: '#64748b' }}>{Math.round(item.prix_unitaire).toLocaleString()}</td>
                                        <td style={{ padding: '10px', textAlign: 'right', fontSize: '13px', fontWeight: '800' }}>{Math.round(item.total_item).toLocaleString()}</td>
                                      </tr>
                                    )) : (
                                      <tr><td colSpan="4" style={{ padding: '20px', textAlign: 'center', color: '#94a3b8', fontStyle: 'italic' }}>Aucun composant configuré</td></tr>
                                    )}
                                  </tbody>
                                </table>
                              </div>
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default ListeFT;